// Centralized language configuration
// This file contains all language-related mappings and configurations

export interface LanguageConfig {
  code: string;           // Language code (e.g., 'en', 'de', 'pt_BR')
  flagId: string;         // Flag ID for flag icons (e.g., 'gb', 'de', 'br')
  name: string;           // Native language name (e.g., 'English', 'Deutsch')
  englishName: string;    // English name (e.g., 'English', 'German')
  description: string;    // Description for settings (e.g., 'Use the app in English')
}

// Available languages - matches the translation files in src/translations/
export const AVAILABLE_LANGUAGES: LanguageConfig[] = [
  // 1) German first
  {
    code: 'de',
    flagId: 'de',
    name: 'Deutsch',
    englishName: 'German',
    description: 'Nutze die App auf Deutsch'
  },
  // 2) English second
  {
    code: 'en',
    flagId: 'gb',
    name: 'English',
    englishName: 'English',
    description: 'Use the app in English'
  },
  // 3) Remaining languages by approximate global speakers
  {
    code: 'es',
    flagId: 'es',
    name: 'Español',
    englishName: 'Spanish',
    description: 'Usa la app en español'
  },
  {
    code: 'fr',
    flagId: 'fr',
    name: 'Français',
    englishName: 'French',
    description: 'Utilisez l\'application en français'
  },
  {
    code: 'pt_BR',
    flagId: 'br',
    name: 'Português (Brasil)',
    englishName: 'Portuguese (Brazil)',
    description: 'Use o app em português brasileiro'
  },
  {
    code: 'ru',
    flagId: 'ru',
    name: 'Русский',
    englishName: 'Russian',
    description: 'Используйте приложение на русском'
  },
  {
    code: 'tr',
    flagId: 'tr',
    name: 'Türkçe',
    englishName: 'Turkish',
    description: 'Uygulamayı Türkçe kullanın'
  },
  {
    code: 'it',
    flagId: 'it',
    name: 'Italiano',
    englishName: 'Italian',
    description: "Usa l'app in italiano"
  },
  {
    code: 'uk',
    flagId: 'ua',
    name: 'Українська',
    englishName: 'Ukrainian',
    description: 'Використовуйте додаток українською'
  },
  {
    code: 'ro',
    flagId: 'ro',
    name: 'Română',
    englishName: 'Romanian',
    description: 'Folosiți aplicația în română'
  },
  {
    code: 'el',
    flagId: 'gr',
    name: 'Ελληνικά',
    englishName: 'Greek',
    description: 'Χρησιμοποιήστε την εφαρμογή στα ελληνικά'
  },
  {
    code: 'hu',
    flagId: 'hu',
    name: 'Magyar',
    englishName: 'Hungarian',
    description: 'Használja az alkalmazást magyarul'
  },
  {
    code: 'sr',
    flagId: 'rs',
    name: 'Srpski',
    englishName: 'Serbian',
    description: 'Koristite aplikaciju na srpskom'
  },
  {
    code: 'sq',
    flagId: 'al',
    name: 'Shqip',
    englishName: 'Albanian',
    description: 'Përdorni aplikacionin në shqip'
  },
  {
    code: 'hr',
    flagId: 'hr',
    name: 'Hrvatski',
    englishName: 'Croatian',
    description: 'Koristite aplikaciju na hrvatskom'
  },
  {
    code: 'pl',
    flagId: 'pl',
    name: 'Polski',
    englishName: 'Polish',
    description: 'Używaj aplikacji po polsku'
  }
];

// Create lookup maps for performance
export const LANGUAGE_BY_CODE = AVAILABLE_LANGUAGES.reduce((acc, lang) => {
  acc[lang.code] = lang;
  return acc;
}, {} as Record<string, LanguageConfig>);

export const LANGUAGE_BY_FLAG_ID = AVAILABLE_LANGUAGES.reduce((acc, lang) => {
  acc[lang.flagId] = lang;
  return acc;
}, {} as Record<string, LanguageConfig>);

// Legacy mappings for backward compatibility
export const LANGUAGE_TO_FLAG_MAP = AVAILABLE_LANGUAGES.reduce((acc, lang) => {
  acc[lang.code] = lang.flagId;
  return acc;
}, {} as Record<string, string>);

export const LANGUAGE_NAMES = AVAILABLE_LANGUAGES.reduce((acc, lang) => {
  acc[lang.code] = lang.name;
  return acc;
}, {} as Record<string, string>);

// Settings page specific mappings (language code to flag ID)
export const LANGUAGE_MAP = LANGUAGE_TO_FLAG_MAP;

// Helper functions
export const getLanguageByCode = (code: string): LanguageConfig | undefined => {
  return LANGUAGE_BY_CODE[code];
};

export const getLanguageByFlagId = (flagId: string): LanguageConfig | undefined => {
  return LANGUAGE_BY_FLAG_ID[flagId];
};

export const getFlagIdByLanguageCode = (code: string): string => {
  return LANGUAGE_TO_FLAG_MAP[code] || 'gb'; // Default to GB flag
};

export const getLanguageName = (code: string): string => {
  return LANGUAGE_NAMES[code] || code.toUpperCase();
};

export const getLanguageDescription = (code: string): string => {
  const lang = getLanguageByCode(code);
  return lang?.description || `Use the app in ${getLanguageName(code)}`;
};

// Get all language codes
export const getAllLanguageCodes = (): string[] => {
  return AVAILABLE_LANGUAGES.map(lang => lang.code);
};

// Get all flag IDs
export const getAllFlagIds = (): string[] => {
  return AVAILABLE_LANGUAGES.map(lang => lang.flagId);
};

// Check if a language code is supported
export const isLanguageSupported = (code: string): boolean => {
  return code in LANGUAGE_BY_CODE;
};

// Get default language
export const getDefaultLanguage = (): string => {
  return 'de';
};
